import { useEffect, useState } from "react";
import axios from "axios";
import { X, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "darkside_promo_banner_dismissed";
const DISMISS_MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12h — reappears next day

/**
 * Slim site-wide promo bar. Fetches the currently featured, active,
 * non-expired promo code and shows it at the very top of every page.
 * - Auto-hides when the API returns null (no featured code).
 * - Auto-hides for 12 h if the user dismisses it (per-browser).
 * - Clicking the code copies it to clipboard + scrolls to the booking form
 *   and dispatches a `prefill-promo` event so the booking form pre-populates.
 */
export default function PromoBanner() {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Respect a recent dismissal
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const savedAt = Number(raw);
        if (Number.isFinite(savedAt) && Date.now() - savedAt < DISMISS_MAX_AGE_MS) {
          setDismissed(true);
          return;
        }
      }
    } catch (_) { /* ignore */ }
    // Fetch featured promo
    axios
      .get(`${API}/promo/featured`)
      .then((r) => {
        setPromo(r.data || null);
        // Fire an impression event once the banner is confirmed live
        if (r.data?.code) {
          axios.post(`${API}/promo/track`, { code: r.data.code, event: "impression" }).catch(() => {});
        }
      })
      .catch(() => setPromo(null));
  }, []);

  if (dismissed || !promo?.code) return null;

  const label = promo.discount_type === "percent"
    ? `${promo.discount_value}% OFF`
    : `$${promo.discount_value} OFF`;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  const useCode = async () => {
    try { await navigator.clipboard?.writeText(promo.code); } catch (_) { /* ignore */ }
    // Track the banner click for analytics
    axios.post(`${API}/promo/track`, { code: promo.code, event: "click" }).catch(() => {});
    // Pre-fill the booking form's promo input via a custom event
    window.dispatchEvent(new CustomEvent("prefill-promo", { detail: { code: promo.code } }));
    // Scroll to booking section
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="w-full bg-[#E30613] text-white text-xs sm:text-sm flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 relative"
      data-testid="promo-banner"
      role="region"
      aria-label="Site promotion"
    >
      <Tag className="w-3.5 h-3.5 flex-shrink-0 hidden sm:block" />
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <span className="font-semibold uppercase tracking-wider">{label}</span>
        {promo.description && (
          <span className="text-white/85 text-[11px] sm:text-xs">
            <span className="hidden sm:inline">&middot; </span>
            {promo.description}
          </span>
        )}
        <button
          type="button"
          onClick={useCode}
          className="inline-flex items-center gap-1.5 bg-white text-[#E30613] font-mono font-semibold px-2 py-0.5 rounded-sm text-[11px] sm:text-xs hover:bg-white/90 active:scale-95 transition-transform"
          data-testid="promo-banner-code"
          aria-label={`Apply promo code ${promo.code}`}
        >
          {promo.code}
        </button>
        <button
          type="button"
          onClick={useCode}
          className="hidden sm:inline underline underline-offset-2 text-white/90 hover:text-white text-[11px]"
          data-testid="promo-banner-cta"
        >
          Use code
        </button>
        {promo.expires_at && (
          <span className="hidden md:inline text-white/70 text-[11px]">&middot; ends {promo.expires_at}</span>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/80 hover:text-white active:scale-95 transition-transform"
        data-testid="promo-banner-dismiss"
        aria-label="Dismiss promotion"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
